import * as React from 'react';
import { NavLink } from 'react-router';
import { cn } from '../lib/cn';
import { navigationPatterns } from '../design-system/patterns/navigation';
import { focusUtils } from '../design-system/utils/a11y';

export interface SubNavItem {
  label: string;
  to: string;
  icon?: React.ComponentType<{ className?: string }>;
  end?: boolean;
  badge?: string | number;
}

export interface UniversalSubNavProps {
  items: SubNavItem[];
  /** Right-side slot for section-level actions (e.g. "Export", "Filter"). */
  right?: React.ReactNode;
  className?: string;
}

/**
 * UniversalSubNav — second-row horizontal navigation for in-section pages.
 *
 * Use when a primary nav item has child pages that benefit from persistent navigation
 * (e.g. Billing → Invoices / Credit Notes / Statements / Payments).
 *
 * For two-level navigation only. If a sub-section has its own children, use
 * route-aware UniversalTabs on the detail page instead of stacking another sub-nav.
 */
export function UniversalSubNav({
  items,
  right,
  className,
}: UniversalSubNavProps) {
  return (
    <div className={cn(navigationPatterns.subNav.wrapper, className)}>
      <nav className="flex items-center gap-1" aria-label="Section">
        <ul className="flex items-center gap-1">
          {items.map((item) => (
            <li key={item.to}>
              <SubNavLink item={item} />
            </li>
          ))}
        </ul>
      </nav>
      {right && <div className="ml-auto flex items-center gap-2">{right}</div>}
    </div>
  );
}

function SubNavLink({ item }: { item: SubNavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          isActive ? navigationPatterns.subNav.linkActive : navigationPatterns.subNav.link,
          focusUtils.ring
        )
      }
    >
      {Icon && <Icon className="h-4 w-4 shrink-0" />}
      <span>{item.label}</span>
      {item.badge !== undefined && (
        <span className="ml-1 rounded-pill bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
          {item.badge}
        </span>
      )}
    </NavLink>
  );
}
